import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { Supertext, Link } from '../../../ui';
import type { SearchHit } from '../../../Search/types';
import { hexToRgba } from '../../../../../features/src/color-conversion';
import {
  IconChevronRight,
  IconPlay,
  Type,
  kleinBlue,
  screenMdMax,
  screenXsMax,
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  heading4FontWeight,
  body3FontSize,
  cta1FontWeight,
  body2FontSize,
} from '@spotify-internal/encore-web';
import { hidden } from '../../util';
import { useT } from '@spotify-internal/i18n-core';

const ItemWrapper = styled(Link)<{
  compact: boolean;
}>`
  background: ${({ theme }) => theme.box.background};
  color: ${({ theme }) => theme.link.color};
  width: calc(25% - ${spacer16});

  &:hover:not(:focus):not(:disabled) {
    color: ${({ theme }) => hexToRgba(theme.link.color, 0.75)};
  }

  @media (max-width: ${screenMdMax}) {
    margin: ${({ compact }) => (compact ? '0' : `0 ${spacer16} ${spacer16} 0`)};
    width: calc(50% - ${spacer24});
  }

  @media (max-width: ${screenXsMax}) {
    width: calc(100% - ${spacer16});
    margin: ${({ compact }) => (compact ? '0' : `0 0 ${spacer16} 0`)};
  }
`;

const TextWrapper = styled.div`
  width: calc(100% - ${spacer16});

  @media (max-width: ${screenMdMax}) {
    width: 100%;
  }

  @media (max-width: ${screenXsMax}) {
    width: 100%;
  }
`;

const Title = styled(Type.h4).attrs({
  variant: 'cello',
})<{
  bold: boolean;
  compact: boolean;
}>`
  font-weight: ${({ bold }) => (bold ? cta1FontWeight : heading4FontWeight)};
  padding-bottom: ${({ compact }) => (compact ? spacer8 : spacer16)};

  @media (max-width: ${screenXsMax}) {
    width: 90%;
    font-weight: ${heading4FontWeight};
  }
`;

const TitleContainer = styled.div<{ largeBottomPadding?: boolean }>`
  display: flex;
  flex-direction: row;
  padding-bottom: ${({ largeBottomPadding }) =>
    largeBottomPadding ? spacer16 : ''};

  @media (max-width: ${screenXsMax}) {
    align-items: center;
    justify-content: space-between;
  }
`;

const StyledSupertext = styled(Supertext)<{
  large: boolean;
  xl?: boolean;
}>`
  font-size: ${({ large, xl }) => {
    if (xl) {
      return spacer32;
    } else if (large) {
      return body2FontSize;
    }
    return body3FontSize;
  }};
  padding-bottom: ${({ large }) => (large ? spacer8 : '0')};
  color: ${({ theme }) => theme.help.grayBox.text};

  @media (max-width: ${screenXsMax}) {
    ${hidden}
  }
`;

const StyledChevronRightIcon = styled(IconChevronRight)`
  display: none;
  [dir='rtl'] & {
    transform: rotateY(180deg);
  }

  @media (max-width: ${screenXsMax}) {
    display: flex;
    align-self: center;
  }
`;

const PlayButton = styled(IconPlay)<{ show?: boolean }>`
  height: ${spacer16};
  width: ${spacer16};
  padding-right: 2px;
  color: ${kleinBlue};
  transform: translateY(2px);
`;

const showPlayButton = (hit: SearchHit, variant: Variant) => {
  return (
    variant === 'related' &&
    (hit.contentType === 'video' || hit.contentType === 'videoPage')
  );
};

const TRACK_CAT = 'helpFAQ';

export enum Variant {
  SPEED_DIAL = 'speedDial',
  RELATED = 'related',
}

const optionsMap = {
  [Variant.SPEED_DIAL]: {
    boldTitle: true,
    largeSuperText: true,
    compact: false,
  },
  [Variant.RELATED]: {
    boldTitle: false,
    largeSuperText: false,
    compact: true,
  },
};

interface FAQItemProps {
  hit: SearchHit;
  variant: Variant;
}

export const FAQItem: React.FC<FAQItemProps> = ({ hit, variant }) => {
  const options = optionsMap[variant];
  const t = useT();
  const router = useRouter();
  const contentTypeLabels: { [key: string]: string } = {
    videoPage: t('FRODOR_51e9b5', 'Video', ''),
    articlePage: t('FRODOR_ae3cce', 'Article', ''),
    default: t('FRODOR_2b4d70', 'Resource', ''),
    faq: t('FRODOR_047e76', 'FAQ', ''),
  };

  // moved logic from content.ts for translation purposes
  if (
    hit.contentType === 'articlePage' ||
    hit.contentType === 'landingPage' ||
    hit.contentType === 'videoPage'
  ) {
    hit.label = contentTypeLabels[hit.contentType] || contentTypeLabels.default;
  } else if (hit.contentType === 'faq' && !hit.label) {
    hit.label = contentTypeLabels.faq;
  }

  return (
    <ItemWrapper
      data-testid="faq-item"
      href={hit.url}
      track={[TRACK_CAT, 'FAQ Link']}
      compact={options.compact}
    >
      {hit.label && hit.title && (
        <TextWrapper>
          <StyledSupertext
            jumbo={variant === 'related'}
            eyebrowPadding={variant === 'related'}
            large={options.largeSuperText}
          >
            {router &&
            router.query &&
            router.query._lqa === 'showFAQItemDefaults'
              ? `${contentTypeLabels.videoPage} ${contentTypeLabels.articlePage} ${contentTypeLabels.default} ${contentTypeLabels.faq}`
              : `${hit.label}`}
          </StyledSupertext>
          <TitleContainer largeBottomPadding={variant === 'related'}>
            <Title compact={options.compact} bold={options.boldTitle}>
              {showPlayButton(hit, variant) && (
                <PlayButton data-testid="play-button" aria-hidden />
              )}
              {hit.title}
            </Title>
            <StyledChevronRightIcon iconSize={16} />
          </TitleContainer>
        </TextWrapper>
      )}
    </ItemWrapper>
  );
};
